import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createTextVideoSkill: Skill = {
    name: SKILL_NAMES.createTextVideo,
    description:
        'Generic video with no narration, told through on-screen text over media. The default recipe whenever the video has no voiceover and no more specific recipe matches.',
    kind: 'video',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Create Text Video (Lumen5 Video Recipe)
Lumen5 best practices for a general-purpose video with **no narration**. This is
the voiceover-free counterpart to \`${SKILL_NAMES.createVideo}\`: same fallback role, same
"teach the viewer something" goal, but the on-screen text carries the whole
message instead of a voice. Load it whenever the video has no voiceover and no
more specific recipe matches.

The one rule everything else follows from: **nobody is speaking, so the text is
the narration.** Every scene has to land on its own, be readable in the time it
is up, and carry the story forward without a narrator to lean on.

## Setup
Load \`${SKILL_NAMES.gatherRequirements}\` and collect the brief in one questionnaire,
skipping anything the user already gave you. Use that skill's general list, and
make sure you come away with:
- **Duration** — this is the word budget for the whole script, so it's needed
  before writing.
- **Closing call-to-action** — should it end on a CTA, or a wrap-up/takeaway?
- **Voice and tone for the script** — free_text. With no preference, derive
  it from audience, platform and subject (e.g. kids → warm and playful, social
  → punchy, B2B → confident and clear) rather than defaulting to corporate.

Two overrides to that skill for this format:
- **Don't ask the \`voice\` question.** This format is silent by definition —
  offering a voice invites an answer that contradicts the recipe. If the user
  does ask for narration mid-build, that's a different format: switch to
  \`${SKILL_NAMES.createVideo}\` and load \`${SKILL_NAMES.voiceoverVideoBasics}\`.
- **Don't ask about captions.** There is no audio to caption.

Pass the aspect ratio to \`${TOOL_NAMES.createBlankVideo}\` when you create the video —
it's a required parameter, so the video is born with the right one. Designs are
laid out per aspect ratio, so a later \`${TOOL_NAMES.setAspectRatio}\` only reworks a
video that is already built. Pick a template matching the subject and requested
tone; search if none of the available ones is an obvious fit.

## Script
If provided an exact script to use, use it.
Otherwise load \`${SKILL_NAMES.writeTextVideoScript}\` before writing any copy: follow it!

The two things that matter most for this recipe:
- **Budget the script to the duration before writing scenes.** Scenes are timed
  off text length, so an over-budget script becomes an over-long video that no
  amount of scene tuning fixes.
- **Hit a duration target with more scenes, never longer ones.** A text video
  gets longer by having more to say, not by holding each thought on screen past
  its reading time.

## Building scenes
Load \`${SKILL_NAMES.textVideoBasics}\` for the mechanics — one thought per scene,
subscenes as the pacing tool (including the \`||\` form of \`${TOOL_NAMES.setText}\`),
auto durations and their 3.4s–7s range, and how to time a deliberate reveal.
Follow it rather than improvising; the rest of this section is what's specific
to building a general-purpose text video.

- **Split into scenes at the level of a thought.** Roughly one sentence or
  sentence fragment per scene, unless you're deliberately holding a beat.
- **Prefer more short scenes to fewer dense ones.** A question and its answer
  are two scenes, not one — the pause between them is the effect. This is the
  single biggest difference between a video that reads as a video and one that
  reads as a slide deck.
- **Open on a title scene** with a specific title saying exactly what the video
  is about. **Close on a real outro** — a CTA if the user asked for one, a
  genuine takeaway if not. Never stop abruptly after the last point.
- For informational content, divide into sections of roughly 10–30 seconds,
  anchored by a heading or landmark scene so the viewer can follow the shape.

## Scene designs
- **Call \`${TOOL_NAMES.listSceneDesigns}\` before picking anything** to see the catalog
  for this template and aspect ratio. Pick from the actual list — don't guess at
  designs or reach for whatever you used last.
- **Rotate across design categories** — title, media, collage, text, quote,
  lists, data/chart, icon. Never the same design on two consecutive scenes,
  with one exception: a list split across scenes (below), where repeating the
  design is the point.
- **Match the design to the content.** A stat gets a data design, a list gets a
  list design. Check each text asset comfortably fits what you're putting in it
  — shape-bounded assets (circles, badges, pills) hold one or two words.
- Intro/title designs only at the open; outro and disclaimer designs only at the
  end. Title designs are also right for chapter breaks between sections.
- **A Quote design needs an actual attributed quotation** with a real name to
  credit. It is not a styling option for a strong line, a tagline, or a
  statistic — the decorative quotation mark is usually a read-only asset, so
  blanking the name/role slots strands it over copy nobody said.
- **List designs are for short items only** — correct only when every item is
  about six words or fewer. A list with longer items becomes one scene per item,
  numbered so the sequence still reads as a list. Never shrink the font or drop
  words to force a long list into one design.
- **A list split across scenes keeps ONE design for every item.** Pick the
  listicle design once and repeat it unchanged for each item, varying only the
  text (and the number). This is the one place the rotation rule above is
  deliberately overridden: the repetition is what tells the viewer these scenes
  are one list rather than unrelated points, and switching designs mid-list
  breaks that thread. The same goes for the media on those scenes — either give
  every item its own media or give none of them any, rather than alternating.
  Resume normal rotation once the list ends.
- **If the copy only fits a design as disconnected fragments, the design is
  wrong** — switch designs rather than degrading the words.

## Media
Media matters more here than in a voiced video: it's the only thing on screen
besides the words, and a run of bare text scenes reads as a slide deck.

- **Prefer a media scene whenever relevant, visually coherent media exists.**
  Fall back to a text-only design when nothing suitable turns up and the text is
  strong enough to carry the scene alone. **Avoid back-to-back text-only
  scenes** — after a text scene, the next one should carry media.
- Load \`${SKILL_NAMES.findUserMedia}\` before going through the user's own library,
  \`${SKILL_NAMES.findStockMedia}\` before your first stock search, and \`${SKILL_NAMES.findDamMedia}\`
  when a DAM is connected — brand and product content likely lives there. The
  user's own material beats stock.
- **Be intentional.** A viewer barely paying attention should immediately get
  why a clip is on screen. Vaguely on-topic is not enough — search again rather
  than settle.
- **Don't reuse stock media across scenes.** Reuse the user's own media only
  when the point genuinely calls for showing the same thing again.
- Reframe within the asset so the subject is well-composed and nothing important
  — especially a face — sits under text (\`${SKILL_NAMES.cropMedia}\` skill).
- **Stats worth seeing become charts**, not plain text.

## Text on screen
- **In a block of 6+ words, highlight the key word or phrase** — with no
  narration to carry emphasis, the highlight is how a viewer parses the line at
  a glance.
- **Don't populate every text asset a design offers.** Pick the design for the
  assets you actually have content for, and blank the rest. A headline plus
  disconnected fragments (eyebrow + headline + subtitle) reads as a presentation
  slide and drags the pace; that's fine for the occasional title scene, not as a
  habit.
- **Replace every placeholder.** Untouched template copy is a defect — on screen
  it's indistinguishable from real copy.
- Aim for a font size around 60 or above, and no emoji (unsupported in renders).

## Music and styling
- **Add background music by default**, matching the requested tone. With no
  narration, silence is very noticeable — a text video without music feels
  broken.
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand
  (\`${SKILL_NAMES.applyBrandKit}\` skill). Don't hand-pick color overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard review procedure and its pass/fail
bar. On top of that, confirm:
- **Every scene stands on its own.** Read each one cold, with no memory of the
  scene before it: does it deliver a complete thought to a viewer with zero
  outside context? A fragment that only means something next to its neighbour is
  a defect here, however well it reads in sequence.
- **Nothing is unreadable in the time it's up.** Scenes sitting near the 7s auto
  cap are carrying too much text — split them rather than extending durations.
- **The pace is brisk but comfortable.** Read a handful of scenes back to back:
  no flicker, no drag. If scenes are running long, look for ones covering more
  than one idea.
- The video rotates across several design categories rather than leaning on two
  or three, and no design repeats back-to-back — except across the scenes of a
  single split list, which should all share one design.
- **A list split across scenes reads as one list**: same design on every item,
  numbering intact, and media applied to all of them or none.
- Media scenes outnumber bare text scenes, and no two text-only scenes sit
  together.
- The open names what the video is about and the close matches the user's CTA
  answer.
- No placeholder or template-default text survives anywhere.`;
    },
};
